// "What rank did this player hold, and when" — the stored history, rendered
// inside the player expansion on the rankings page.
//
// WHY IT IS A SEPARATE FETCH, AND A LAZY ONE. The standings come from
// /api/rankings/bundle, which has no history in it at all — it is recomputed
// per request and never persisted. History lives in the snapshot artifacts and
// is one request PER PLAYER, so fetching it for a 655-row table on page load
// would be 655 requests to render a page nobody has expanded yet. It is fetched
// when a row is opened, once, and cached on the element.
//
// THE EMPTY CASE IS THE COMMON ONE TODAY, and it is not an error. Ranking
// snapshots are generated by an explicit step, not on ingest — production
// currently holds two, both with entryCount 0 — so most players legitimately
// have no published history yet. That renders as a plain statement of fact
// rather than a failure, because a red error box for "the lists have not been
// generated" would be both wrong and alarming.

import {
  describeRankingList,
  fetchPersonRankingHistory,
  PersonRankingRow,
  rankingListPath,
} from 'src/services/api/rankingsApi';

const HISTORY_PAGE_SIZE = 60;
const SVG_NS = 'http://www.w3.org/2000/svg';

/** Cache per person so collapsing and re-expanding a row does not re-fetch. */
const cache = new Map<string, Promise<PersonRankingRow[]>>();

function loadHistory(personId: string): Promise<PersonRankingRow[]> {
  let pending = cache.get(personId);
  if (!pending) {
    pending = fetchPersonRankingHistory(personId, { limit: HISTORY_PAGE_SIZE })
      .then((paged) => paged.data)
      .catch((e) => {
        // Do not poison the cache — a transient failure should be retryable by
        // reopening the row.
        cache.delete(personId);
        throw e;
      });
    cache.set(personId, pending);
  }
  return pending;
}

/** Exposed for tests; the module-level cache would otherwise leak between them. */
export function clearRankingHistoryCache(): void {
  cache.clear();
}

/**
 * A rank-over-time sparkline, drawn inline.
 *
 * Inline SVG rather than a charting component because none of the three charts
 * courthive-components exports fits: they plot categorical rungs, pressure and
 * rating distributions. Rank is numeric, inverted (1 is the top), and this is a
 * dozen points in a table cell — a dependency would cost more than it saves.
 *
 * `currentColor` throughout, so it themes with the surrounding text in both
 * light and dark without a second set of tokens.
 */
function buildSparkline(rows: PersonRankingRow[]): SVGSVGElement | null {
  if (rows.length < 2) return null;

  // Oldest → newest for reading left to right; the API returns newest first.
  const points = [...rows].reverse().map((r) => r.entry.rank);
  const best = Math.min(...points);
  const worst = Math.max(...points);
  const span = worst - best || 1;

  const width = 160;
  const height = 28;
  const step = width / (points.length - 1);

  // Inverted: rank 1 is the BEST, so it belongs at the TOP of the sparkline.
  const coords = points.map((rank, i) => `${(i * step).toFixed(1)},${(((rank - best) / span) * (height - 4) + 2).toFixed(1)}`);

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.setAttribute('class', 'rk-history-spark');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `Rank over time, best ${best}, worst ${worst}`);

  const path = document.createElementNS(SVG_NS, 'polyline');
  path.setAttribute('points', coords.join(' '));
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.5');
  path.setAttribute('stroke-linejoin', 'round');
  path.setAttribute('stroke-linecap', 'round');
  svg.appendChild(path);

  const last = coords[coords.length - 1].split(',');
  const dot = document.createElementNS(SVG_NS, 'circle');
  dot.setAttribute('cx', last[0]);
  dot.setAttribute('cy', last[1]);
  dot.setAttribute('r', '2.5');
  dot.setAttribute('fill', 'currentColor');
  svg.appendChild(dot);

  return svg;
}

function buildHistoryTable(rows: PersonRankingRow[]): HTMLElement {
  const table = document.createElement('table');
  table.className = 'rk-detail-table rk-history-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th>As of</th>
        <th>Ranking list</th>
        <th class="rk-col-pts">Rank</th>
        <th class="rk-col-pts">Points</th>
        <th>Full list</th>
      </tr>
    </thead>
  `;

  const body = document.createElement('tbody');
  for (const row of rows) {
    const tr = document.createElement('tr');

    const asOf = document.createElement('td');
    asOf.textContent = row.snapshot.asOfDate;
    tr.appendChild(asOf);

    const list = document.createElement('td');
    list.textContent = describeRankingList(row.snapshot);
    tr.appendChild(list);

    const rank = document.createElement('td');
    rank.className = 'rk-col-pts';
    // "47 of 20,805" — a rank without the size of the field it came from is
    // not a fact anyone can interpret.
    rank.textContent = row.snapshot.entryCount
      ? `${row.entry.rank} of ${row.snapshot.entryCount.toLocaleString()}`
      : String(row.entry.rank);
    tr.appendChild(rank);

    const points = document.createElement('td');
    points.className = 'rk-col-pts';
    points.textContent = String(row.entry.totalPoints);
    tr.appendChild(points);

    // The link the whole exercise exists for: from "I was 47th on 12 August"
    // to the list that says so.
    const link = document.createElement('td');
    const anchor = document.createElement('a');
    anchor.className = 'rk-history-link';
    anchor.href = `#${rankingListPath(row.snapshot.snapshotId)}`;
    anchor.textContent = 'View list';
    link.appendChild(anchor);
    tr.appendChild(link);

    body.appendChild(tr);
  }
  table.appendChild(body);
  return table;
}

/**
 * Builds the history block for one player. Renders a placeholder immediately
 * and fills it in when the fetch resolves, so opening a row is never blocked on
 * the network.
 */
export function buildRankingHistorySection(personId: string, playerName: string): HTMLElement {
  const section = document.createElement('div');
  section.className = 'rk-history';

  const header = document.createElement('div');
  header.className = 'rk-detail-header';
  header.textContent = `${playerName} · ranking history`;
  section.appendChild(header);

  const body = document.createElement('div');
  body.className = 'rk-history-body';
  body.textContent = 'Loading history…';
  section.appendChild(body);

  loadHistory(personId)
    .then((rows) => {
      body.textContent = '';
      if (!rows.length) {
        const empty = document.createElement('div');
        empty.className = 'rk-history-empty';
        empty.textContent = 'No published ranking lists include this player yet.';
        body.appendChild(empty);
        return;
      }

      const spark = buildSparkline(rows);
      if (spark) {
        const sparkWrap = document.createElement('div');
        sparkWrap.className = 'rk-history-spark-wrap';
        sparkWrap.appendChild(spark);
        const caption = document.createElement('span');
        caption.className = 'rk-history-spark-caption';
        // Newest first, so [0] is current and the last element is the oldest.
        caption.textContent = `Rank over ${rows.length} published lists · now ${rows[0].entry.rank}, was ${rows[rows.length - 1].entry.rank}`;
        sparkWrap.appendChild(caption);
        body.appendChild(sparkWrap);
      }

      body.appendChild(buildHistoryTable(rows));
    })
    .catch(() => {
      body.textContent = '';
      const failed = document.createElement('div');
      failed.className = 'rk-history-empty';
      failed.textContent = 'Ranking history is unavailable right now.';
      body.appendChild(failed);
    });

  return section;
}
